import React from 'react';
import autobind from 'autobind-decorator';
import { Grid, Cell, Offset } from 'react-ittsu/layout';
import gql from 'fraql';

import BaseServiceModal from '../BaseServiceModal';
import Paragraph from '../Paragraph';
import SectionTitle from '../SectionTitle';
import { ProductCard, Stripe, StripeGroup } from '../../forms';
import { priceItem, applyPrice } from '../../../utils/pricing';
import { specsToItems, itemsToSpecs } from '~/pods/order/utils/services/specs-to-items';
import applyChanges from '~/pods/order/utils/services/specs-to-items/apply-changes';
import { getItemsByService, merge } from '~/pods/order/utils';
import { createTranslate, translate as t } from '~/utils/translation';

import serviceImages from '~/pods/order/images/services';


const tt = createTranslate('pods.order.services.photo_editing.order');
const tm = createTranslate('pods.order.components.service_modal');


class PhotoEditing extends React.Component {

  static fragments = {
    Item: gql`
      fragment _ on Item {
        id
        details {
          ... on PhotoEditingDetails {
            numberOfImages
            logoAndWatermark
          }
        }
      }
    `,
  }

  state = {
    specs: {
      logoAndWatermark: false,
      numberOfImages: 0,
      standard: 0,
    },
    price: undefined,
  };

  componentDidUpdate(prevProps) {
    const { specs } = this.state;
    const { service, estate } = this.props;
    const { service: prevService } = prevProps;
    if (service?.id === 'photoEditing' && ! prevService) {
      const newSpecs = itemsToSpecs({ estate, service });
      const items = getItemsByService(estate).photoEditing;
      const price = items != null ? items.reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0) : 0;
      this.setState({ specs: merge(specs, newSpecs), price });
    }
  }

  render() {
    const { onClickClose, isVisible, service, estate, sessionUser } = this.props;
    const { specs: { standard, numberOfImages, logoAndWatermark }, price } = this.state;
    const { photoEditing } = serviceImages;
    const items = getItemsByService(estate).photoEditing;
    return (
      <BaseServiceModal
        isVisible={isVisible && service?.id === 'photoEditing'}
        title={service?.id ? t(`services.${service.id}`) : ''}
        images={Object.values(photoEditing)}
        price={sessionUser.addCurrency(price)}
        onClickConfirm={this._handleClickConfirm}
        confirmAsUpdate={items?.length > 0}
        onClickClose={onClickClose}
        addDisabled={price === 0}>
        <div className="childcontent">
          <Paragraph>
            {tt('description')}
          </Paragraph>
          <SectionTitle>
            {tt('select_number')}
          </SectionTitle>
          <Grid withVGutters withHGutters>
            <Offset size="1of4" />
            <Cell size="2of4">
              <ProductCard
                name="standard"
                label={tm('standard')}
                note={`${applyPrice({ EUR: 2, GBP: 0, USD: 0 }, sessionUser)}/img`}
                fullHeight
                formComponent="radio"
                features={[{
                  text: tt('features.first'),
                }]}
                value={numberOfImages}
                onChange={(v) => this._handleClickOption('numberOfImages', v)}
                withCounter />
            </Cell>
            <Offset size="1of4" />
          </Grid>
          <SectionTitle>
            {tt('customize')}
          </SectionTitle>
          <StripeGroup>
            <Stripe
              title={tt('logo_and_watermark')}
              component="switch"
              features={[{
                text: tt('logo_and_w_features.first'),
              }]}
              disabled={! standard}
              value={logoAndWatermark}
              onChange={() => this._handleChangeSpec('logoAndWatermark', ! logoAndWatermark)} />
          </StripeGroup>
        </div>
      </BaseServiceModal>
    );
  }

  @autobind
  _syncState() {
    const { estate, service } = this.props;
    const { specs } = this.state;
    const changes = specsToItems({ specs, estate, service });
    const updatedEstate = applyChanges(changes, estate);
    const items = getItemsByService(updatedEstate).photoEditing;
    const price = items != null
      ? items
          .map((item) => item.estate != null ? item : { ...item, estate: updatedEstate })
          .reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0)
      : 0;
    this.setState({ price });
    return { changes, updatedEstate, price };
  }

  @autobind
  _handleClickOption(key, value) {
    const { specs } = this.state;
    const { logoAndWatermark } = specs;
    const newValue = value ? value : (specs[key] !== 0 ? 0 : 1);
    const newSpecs = {
      ...specs,
      standard: newValue !== 0,
      [key]: newValue,
    };
    const disableOptions =! newSpecs.standard;
    this.setState({
      specs: {
        ...newSpecs,
        logoAndWatermark: disableOptions ? false : logoAndWatermark,
      }
    }, this._syncState);
  }

  @autobind
  _resetSpecs(onReset) {
    this.setState({ specs: { standard: false, logoAndWatermark: false, numberOfImages: 0 } }, onReset);
  }

  @autobind
  _handleChangeSpec(specKey, specValue) {
    this.setState({ specs: { ...this.state.specs, [specKey]: specValue, } }, this._syncState);
  }

  @autobind
  _handleClickConfirm() {
    const { onModifyService, service } = this.props;
    const { changes } = this._syncState();
    onModifyService(changes, service);
  }
}


export default PhotoEditing;
